import { useEffect, useState } from 'react'
import '../styles/animations.css'

type AnimationType = 'quantum' | 'neural' | 'photon' | 'cube'

interface AnimatedTextProps {
  words: string[]
  interval?: number
  animation?: AnimationType
  className?: string
}

export function AnimatedText({
  words,
  interval = 3000,
  animation = 'quantum',
  className = '',
}: AnimatedTextProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    const timer = setInterval(() => {
      setIsAnimating(true)
      setTimeout(
        () => {
          setCurrentIndex((prev) => (prev + 1) % words.length)
          setIsAnimating(false)
        },
        animation === 'photon' ? 200 : 400,
      )
    }, interval)

    return () => clearInterval(timer)
  }, [words.length, interval, animation])

  function getAnimationClasses(wordIndex: number) {
    const isActive = wordIndex === currentIndex
    const baseClasses = 'animation-base'

    if (isActive && !isAnimating) {
      return `${baseClasses} ${animation}-active`
    }
    return `${baseClasses} ${animation}-inactive`
  }

  function renderCubeAnimation() {
    return (
      <span className={`inline-block ${className}`}>
        <div className='cube-container'>
          <div className={`cube-inner ${isAnimating ? 'flipped' : ''}`}>
            <div className='cube-face front'>{words[currentIndex]}</div>
            <div className='cube-face back'>
              {words[(currentIndex + 1) % words.length]}
            </div>
          </div>
        </div>
        <span className='invisible'>{words[0]}</span>
      </span>
    )
  }

  function renderStandardAnimation() {
    const longestWord = words.reduce((longest, current) =>
      current.length > longest.length ? current : longest,
    )

    return (
      <span className={`inline-block relative ${className}`}>
        {words.map((word, index) => (
          <span key={`${word}-${index}`} className={getAnimationClasses(index)}>
            {word}
          </span>
        ))}
        <span className='invisible'>{longestWord}</span>
      </span>
    )
  }

  if (animation === 'cube') {
    return renderCubeAnimation()
  }

  return renderStandardAnimation()
}
